import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { UserX, ShieldOff } from 'lucide-react';
import { Button } from '../ui/button';
import Spinner from '../UIFeedback/Spinner/Spinner';
import { UNBLOCK_USER } from '../../services/apollo/mutations/blockMutations';
import {
  selectBlockedUsersList,
  fetchBlockedUsers,
  removeBlockedUser,
  addBlockedUser,
} from '../../slices/blocksSlice';
import { formatDate } from '../../hooks/utils';

const BlockedUsersList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const blockedUsers = useSelector(selectBlockedUsersList);
  const loading = useSelector((state) => state.blocks.loading);

  const [unblockMutation] = useMutation(UNBLOCK_USER);

  useEffect(() => {
    dispatch(fetchBlockedUsers());
  }, [dispatch]);

  const handleUnblock = async (userId, originalUser) => {
    dispatch(removeBlockedUser(userId));
    try {
      await unblockMutation({ variables: { userId } });
    } catch (err) {
      dispatch(addBlockedUser(originalUser));
      console.error('Failed to unblock:', err);
    }
  };

  if (loading && blockedUsers.length === 0) {
    return (
      <div className="flex items-center justify-center py-10">
        <Spinner />
      </div>
    );
  }

  if (!blockedUsers.length) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-10 px-4">
        <div className="w-14 h-14 rounded-full bg-secondary/40 flex items-center justify-center mb-3">
          <ShieldOff className="w-7 h-7 text-muted-foreground" />
        </div>
        <h4 className="text-base font-semibold text-foreground mb-1">
          {t('blocked_users_empty_title', 'No blocked users')}
        </h4>
        <p className="text-sm text-muted-foreground max-w-xs">
          {t(
            'blocked_users_empty_subtitle',
            "When you block someone, they'll appear here. You can unblock them anytime."
          )}
        </p>
      </div>
    );
  }

  return (
    <ul className="bg-white rounded-lg border border-border divide-y divide-border overflow-hidden">
      {blockedUsers.map((u) => (
        <li
          key={u._id}
          className="flex items-center justify-between gap-3 p-3 sm:p-4"
        >
          <div className="min-w-0 flex-1 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-secondary/40 flex items-center justify-center flex-shrink-0">
              <UserX className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground truncate">
                @{u.username}
              </p>
              {u.blockedAt && (
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  {t('blocked_on', 'Blocked on')} {formatDate(u.blockedAt)}
                </p>
              )}
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleUnblock(u._id, u)}
            className="flex-shrink-0"
          >
            {t('unblock', 'Unblock')}
          </Button>
        </li>
      ))}
    </ul>
  );
};

export default BlockedUsersList;
